import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, query, limit as qlimit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';

interface EntityRow {
  id: string;
  legalName?: string;
  shortName?: string;
  type?: string;
  status?: string;
  jurisdiction?: string;
  ein?: string;
  mergedInto?: string;
}

const TYPES = ['LP', 'LLC', 'Trust', 'Corp', 'Individual', 'Other'] as const;

/**
 * Entity master management. Lists entities, shows live document counts per
 * entity (tallied from documents.filingEntity), and supports add / edit /
 * rename / merge / delete plus a one-click "Seed from documents".
 */
export function EntitiesList() {
  const { role } = useAuth();
  const [entities, setEntities] = useState<EntityRow[]>([]);
  const [docCounts, setDocCounts] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [editing, setEditing] = useState<EntityRow | null>(null);
  const [mergeSource, setMergeSource] = useState<string>('');
  const [mergeTarget, setMergeTarget] = useState<string>('');

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'entities'), qlimit(1000)), (snap) => {
      setEntities(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<EntityRow, 'id'>) })));
    });
    // Tally docs by filingEntity for live counts.
    const unsub2 = onSnapshot(query(collection(db, 'documents'), qlimit(2000)), (snap) => {
      const t: Record<string, number> = {};
      for (const d of snap.docs) {
        const fe = ((d.get('filingEntity') as string) || '').trim() || 'Unassigned';
        t[fe] = (t[fe] ?? 0) + 1;
      }
      setDocCounts(t);
    });
    return () => {
      unsub();
      unsub2();
    };
  }, []);

  const rows = useMemo(
    () =>
      entities
        .filter((e) => e.status !== 'dissolved')
        .sort((a, b) => (a.legalName ?? a.id).localeCompare(b.legalName ?? b.id)),
    [entities],
  );

  // Entities discovered on documents but not yet in the master.
  const unmastered = useMemo(() => {
    const known = new Set(entities.map((e) => (e.legalName ?? '').trim()));
    return Object.entries(docCounts)
      .filter(([name]) => name !== 'Unassigned' && !known.has(name))
      .sort((a, b) => b[1] - a[1]);
  }, [docCounts, entities]);

  if (role !== 'admin') {
    return <div className="mx-auto max-w-xl px-6 py-12 text-sm text-stone-500">Admins only.</div>;
  }

  async function call(name: string, data: unknown, okMsg: (r: any) => string) {
    setBusy(true);
    setMsg(null);
    try {
      const r = await httpsCallable(functions, name)(data);
      setMsg(okMsg(r.data));
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-6 py-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-stone-400">Module 2</div>
          <h1 className="text-2xl font-semibold text-stone-800">Entities</h1>
          <p className="text-sm text-stone-500">
            The entity master. Documents file under these names — rename or merge to consolidate, and
            it flows through to the <Link to="/documents/library" className="text-[color:var(--sfp-primary)] hover:underline">Library</Link>.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              call('seedEntitiesFromDocuments', {}, (r) => `Seeded ${r.upserted} of ${r.distinctEntities} entities.`)
            }
            className="rounded bg-[color:var(--sfp-primary)] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
          >
            Seed from documents
          </button>
          <button
            type="button"
            onClick={() => setEditing({ id: '', legalName: '', type: 'Other', status: 'active' })}
            className="rounded border border-stone-300 px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50"
          >
            + Add entity
          </button>
        </div>
      </header>

      {msg && <div className="rounded border border-stone-200 bg-stone-50 px-3 py-2 text-xs text-stone-700">{msg}</div>}

      {unmastered.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div className="text-xs font-semibold text-amber-800">
            {unmastered.length} entit{unmastered.length === 1 ? 'y' : 'ies'} found on documents but not in the master
          </div>
          <div className="mt-1 text-[11px] text-amber-700">
            {unmastered.slice(0, 10).map(([n, c]) => `${n} (${c})`).join(' · ')}
            {unmastered.length > 10 ? ' …' : ''}
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              call('seedEntitiesFromDocuments', {}, (r) => `Seeded ${r.upserted} entities.`)
            }
            className="mt-2 rounded bg-amber-700 px-2.5 py-1 text-[11px] font-medium text-white disabled:opacity-50"
          >
            Add them all
          </button>
        </div>
      )}

      {/* Merge tool */}
      <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
        <div className="text-[10px] uppercase tracking-wider text-stone-400">Merge / consolidate</div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          <select value={mergeSource} onChange={(e) => setMergeSource(e.target.value)} className="rounded border border-stone-300 px-2 py-1.5">
            <option value="">Merge this…</option>
            {rows.map((e) => (
              <option key={e.id} value={e.id}>{e.legalName} ({docCounts[e.legalName ?? ''] ?? 0})</option>
            ))}
          </select>
          <span className="text-stone-400">→ into →</span>
          <select value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)} className="rounded border border-stone-300 px-2 py-1.5">
            <option value="">…this</option>
            {rows.map((e) => (
              <option key={e.id} value={e.id}>{e.legalName}</option>
            ))}
          </select>
          <button
            type="button"
            disabled={busy || !mergeSource || !mergeTarget || mergeSource === mergeTarget}
            onClick={() => {
              if (!confirm('Merge these entities? All documents under the source move to the target.')) return;
              call('mergeEntities', { sourceEntityId: mergeSource, targetEntityId: mergeTarget }, (r) => `Moved ${r.docsMoved} doc(s).`);
              setMergeSource('');
              setMergeTarget('');
            }}
            className="rounded bg-[#1F3357] px-3 py-1.5 font-medium text-white disabled:opacity-40"
          >
            Merge
          </button>
        </div>
      </div>

      {/* Entity table */}
      <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-stone-200 bg-stone-50 text-left text-[10px] uppercase tracking-wider text-stone-500">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Type</th>
              <th className="px-4 py-2">Docs</th>
              <th className="px-4 py-2">Jurisdiction</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-xs text-stone-400">No entities yet. Click “Seed from documents”.</td></tr>
            ) : rows.map((e) => (
              <tr key={e.id} className="border-b border-stone-100 last:border-b-0">
                <td className="px-4 py-2">
                  <Link to={`/documents/library?entity=${encodeURIComponent(e.legalName ?? '')}`} className="font-medium text-stone-800 hover:underline">
                    {e.legalName}
                  </Link>
                </td>
                <td className="px-4 py-2"><span className="rounded bg-stone-100 px-2 py-0.5 text-[11px] text-stone-700">{e.type}</span></td>
                <td className="px-4 py-2 text-stone-600">{docCounts[e.legalName ?? ''] ?? 0}</td>
                <td className="px-4 py-2 text-xs text-stone-500">{e.jurisdiction ?? '—'}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => setEditing(e)} className="text-xs text-[color:var(--sfp-primary)] hover:underline">Edit</button>
                  <button
                    onClick={() => {
                      const nn = prompt('Rename entity to:', e.legalName ?? '');
                      if (nn && nn.trim() && nn.trim() !== e.legalName) {
                        call('renameEntity', { entityId: e.id, newName: nn.trim() }, (r) => `Renamed; ${r.docsRewritten} doc(s) updated.`);
                      }
                    }}
                    className="ml-3 text-xs text-stone-600 hover:underline"
                  >
                    Rename
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Delete entity "${e.legalName}"? Documents keep their filing name.`)) {
                        call('deleteEntity', { entityId: e.id }, () => 'Deleted.');
                      }
                    }}
                    className="ml-3 text-xs text-[#9C4A3A] hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EntityEditor
          entity={editing}
          onClose={() => setEditing(null)}
          onSave={(payload) =>
            call('setEntity', payload, () => 'Saved.').then(() => setEditing(null))
          }
        />
      )}
    </div>
  );
}

function EntityEditor({
  entity,
  onClose,
  onSave,
}: {
  entity: EntityRow;
  onClose: () => void;
  onSave: (payload: Record<string, unknown>) => void;
}) {
  const [legalName, setLegalName] = useState(entity.legalName ?? '');
  const [shortName, setShortName] = useState(entity.shortName ?? '');
  const [type, setType] = useState(entity.type ?? 'Other');
  const [jurisdiction, setJurisdiction] = useState(entity.jurisdiction ?? '');
  const [ein, setEin] = useState(entity.ein ?? '');

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <h2 className="text-sm font-semibold text-stone-800">{entity.id ? 'Edit entity' : 'Add entity'}</h2>
        <div className="mt-4 space-y-3 text-xs">
          <label className="flex flex-col">
            <span className="text-stone-500">Legal name</span>
            <input value={legalName} onChange={(e) => setLegalName(e.target.value)} className="rounded border border-stone-300 px-2 py-1.5 text-sm" />
          </label>
          <label className="flex flex-col">
            <span className="text-stone-500">Short name</span>
            <input value={shortName} onChange={(e) => setShortName(e.target.value)} className="rounded border border-stone-300 px-2 py-1.5 text-sm" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col">
              <span className="text-stone-500">Type</span>
              <select value={type} onChange={(e) => setType(e.target.value)} className="rounded border border-stone-300 px-2 py-1.5 text-sm">
                {TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </label>
            <label className="flex flex-col">
              <span className="text-stone-500">Jurisdiction</span>
              <input value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)} className="rounded border border-stone-300 px-2 py-1.5 text-sm" placeholder="DE, NY…" />
            </label>
          </div>
          <label className="flex flex-col">
            <span className="text-stone-500">EIN (optional)</span>
            <input value={ein} onChange={(e) => setEin(e.target.value)} className="rounded border border-stone-300 px-2 py-1.5 text-sm" />
          </label>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-xs text-stone-500 hover:text-stone-800">Cancel</button>
          <button
            disabled={!legalName.trim()}
            onClick={() =>
              onSave({
                id: entity.id || undefined,
                legalName: legalName.trim(),
                shortName: shortName.trim() || legalName.trim(),
                type,
                jurisdiction: jurisdiction.trim() || undefined,
                ein: ein.trim() || undefined,
                status: 'active',
              })
            }
            className="rounded bg-[color:var(--sfp-primary)] px-4 py-1.5 text-xs font-medium text-white disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
